/**
 * Real gem-art card assets: a parchment frame per jewel (bordered, jewel name
 * baked in top/bottom), a faceted gem-cut suit icon per jewel×suit
 * combination, and an engraved rank glyph per rank. Composited together in
 * PlayingCard.tsx. Source art: gem-themed cutout pipeline, see
 * memory/project_poker3_card_assets.md for the background-removal approach.
 */
import type { Jewel, Rank, SpecialCardType, Suit } from '@pokercubed/shared';

import frameRuby from './frames/ruby.png';
import frameEmerald from './frames/emerald.png';
import frameTopaz from './frames/topaz.png';
import frameOnyx from './frames/onyx.png';

import rubyHearts from './icons/ruby-hearts.png';
import rubyDiamonds from './icons/ruby-diamonds.png';
import rubyClubs from './icons/ruby-clubs.png';
import rubySpades from './icons/ruby-spades.png';
import emeraldHearts from './icons/emerald-hearts.png';
import emeraldDiamonds from './icons/emerald-diamonds.png';
import emeraldClubs from './icons/emerald-clubs.png';
import emeraldSpades from './icons/emerald-spades.png';
import topazHearts from './icons/topaz-hearts.png';
import topazDiamonds from './icons/topaz-diamonds.png';
import topazClubs from './icons/topaz-clubs.png';
import topazSpades from './icons/topaz-spades.png';
import onyxHearts from './icons/onyx-hearts.png';
import onyxDiamonds from './icons/onyx-diamonds.png';
import onyxClubs from './icons/onyx-clubs.png';
import onyxSpades from './icons/onyx-spades.png';

import cardBack from './card-back.webp';
import specialBlank from './special/blank.webp';
import specialBack from './special/special-face-down.webp';
import specialGhost from './special/ghost.webp';
import specialChameleon from './special/chameleon.webp';
import specialNumerology from './special/numerology.webp';
import specialSuitor from './special/suitor.webp';
import specialImpersonator from './special/impersonator.webp';
import specialThief from './special/thief.webp';
import specialFlawed from './special/flawed.webp';
import specialFray from './special/fray.webp';
import specialOrphan from './special/orphan.webp';
import specialVoid from './special/void.webp';
import specialOmnipotent from './special/omnipotent.webp';
import specialSpy from './special/spy.webp';
import specialJeweler from './special/jeweler.webp';
import specialJoker from './special/joker.webp';
import specialHighflyer from './special/highflyer.webp';
import specialSeer from './special/seer.webp';
import specialSoothsayer from './special/soothsayer.webp';
import specialAnchor from './special/anchor.webp';
import specialOracle from './special/oracle.webp';
import specialPrank from './special/prank.webp';
import specialBlind from './special/blind.webp';
import specialBuried from './special/buried.webp';
import specialLimbo from './special/limbo.webp';
import specialHellraiser from './special/hellraiser.webp';
import specialOrigami from './special/origami.webp';
import specialCrossed from './special/crossed.webp';
import specialSnare from './special/snare.webp';
// Shown as Shepherd (the card's name since its art was made).
import specialCommentator from './special/commentator.webp';

import rankA from './ranks/A.png';
import rank2 from './ranks/2.png';
import rank3 from './ranks/3.png';
import rank4 from './ranks/4.png';
import rank5 from './ranks/5.png';
import rank6 from './ranks/6.png';
import rank7 from './ranks/7.png';
import rank8 from './ranks/8.png';
import rank9 from './ranks/9.png';
import rank10 from './ranks/10.png';
import rankJ from './ranks/J.png';
import rankQ from './ranks/Q.png';
import rankK from './ranks/K.png';

export const FRAME_ART: Record<Jewel, string> = {
  ruby: frameRuby,
  emerald: frameEmerald,
  topaz: frameTopaz,
  onyx: frameOnyx,
} as Record<Jewel, string>;

export const ICON_ART: Record<Jewel, Record<Suit, string>> = {
  ruby: { hearts: rubyHearts, diamonds: rubyDiamonds, clubs: rubyClubs, spades: rubySpades },
  emerald: { hearts: emeraldHearts, diamonds: emeraldDiamonds, clubs: emeraldClubs, spades: emeraldSpades },
  topaz: { hearts: topazHearts, diamonds: topazDiamonds, clubs: topazClubs, spades: topazSpades },
  onyx: { hearts: onyxHearts, diamonds: onyxDiamonds, clubs: onyxClubs, spades: onyxSpades },
} as Record<Jewel, Record<Suit, string>>;

export const RANK_ART: Record<Rank, string> = {
  A: rankA,
  '2': rank2,
  '3': rank3,
  '4': rank4,
  '5': rank5,
  '6': rank6,
  '7': rank7,
  '8': rank8,
  '9': rank9,
  '10': rank10,
  J: rankJ,
  Q: rankQ,
  K: rankK,
} as Record<Rank, string>;

/**
 * Each special's finished card, shown as an extra card beside the hole
 * cards. Translucent: the card face and soft art (e.g. the Ghost) are
 * see-through, so the table shows through them. Built by
 * special/build_special_cards.py (full cards via translucent_card.py); the
 * name is shown above the card by the app, not baked in. Cards without art
 * yet use SPECIAL_BLANK_ART.
 */
export const SPECIAL_CARD_ART: Partial<Record<SpecialCardType, string>> = {
  ghost: specialGhost,
  chameleon: specialChameleon,
  numerology: specialNumerology,
  suitor: specialSuitor,
  impersonator: specialImpersonator,
  thief: specialThief,
  flawed: specialFlawed,
  fray: specialFray,
  orphan: specialOrphan,
  void: specialVoid,
  omnipotent: specialOmnipotent,
  spy: specialSpy,
  jeweler: specialJeweler,
  joker: specialJoker,
  highflyer: specialHighflyer,
  seer: specialSeer,
  soothsayer: specialSoothsayer,
  anchor: specialAnchor,
  oracle: specialOracle,
  prank: specialPrank,
  blind: specialBlind,
  buried: specialBuried,
  limbo: specialLimbo,
  hellraiser: specialHellraiser,
  origami: specialOrigami,
  crossed: specialCrossed,
  snare: specialSnare,
  commentator: specialCommentator,
} as Partial<Record<SpecialCardType, string>>;

/** The plain Special card, for a special whose art isn't made yet. */
export const SPECIAL_BLANK_ART: string = specialBlank;

/** The back of every face-down card: the PokerCubed logo (from "P3 Logo.jpg", cropped to 5:7). */
export const CARD_BACK_ART: string = cardBack;

/** A special not yet turned over (until the 4 corners are revealed): the gold "Special ?" card ("Special Card.jpg", cut out and sized to the hole-card box). */
export const SPECIAL_BACK_ART: string = specialBack;
